import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import logger from '../utils/logger';
import LastUpdatedIndicator from './LastUpdatedIndicator';
import './SubscriptionHealthWidget.css';

const API = process.env.REACT_APP_BACKEND_URL;

const WARNING_COPY = {
  db_says_active_but_stripe_not: 'Your account shows active but Stripe does not confirm. Please contact support.',
  stripe_active_but_db_not_reconciled: 'We just reconciled your subscription. Please refresh.',
  past_payer_with_no_skins: 'You have purchase history but no skins on record. Please contact support.'
};

const formatDate = (epoch) => {
  if (!epoch) return null;
  try {
    const d = new Date(epoch * 1000);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
};

const formatPlan = (amount, currency, interval) => {
  if (!amount) return null;
  const price = (amount / 100).toFixed(2);
  return `${currency?.toUpperCase() || 'USD'} $${price} / ${interval || 'month'}`;
};

/**
 * Compact subscription health widget.
 * Shows: plan, next renewal, owned skin count, and a "Run health check"
 * button that hits /api/subscription/health (which includes Stripe self-heal).
 */
const SubscriptionHealthWidget = () => {
  const [status, setStatus] = useState(null);   // /api/subscription/status payload
  const [health, setHealth] = useState(null);   // /api/subscription/health payload
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/subscription/status`);
      setStatus(data);
    } catch (err) {
      logger.error('[SubscriptionHealthWidget] status load failed', err);
    }
  }, []);

  const runHealthCheck = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/api/subscription/health`);
      setHealth(data);
      // Stripe self-heal may have reconciled — refresh status display too
      await loadStatus();
    } catch (err) {
      logger.error('[SubscriptionHealthWidget] health check failed', err);
      setError('Health check failed. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [loadStatus]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const subscribed = !!status?.subscribed;
  // For non-subscribers, /api/subscription/status returns a default price (the
  // upsell price) — only show the Plan/Renewal cells when actually subscribed.
  const planLabel = (status && subscribed) ? formatPlan(status.plan_amount, status.plan_currency, status.plan_interval) : null;
  const nextRenewal = (status && subscribed) ? formatDate(status.current_period_end) : null;
  const cancelAtEnd = !!status?.cancel_at_period_end;

  return (
    <div className="subhealth-widget" data-testid="subscription-health-widget">
      <div className="subhealth-header">
        <h3 className="subhealth-title">
          <span className="subhealth-icon" aria-hidden="true">🛡️</span>
          My Account
        </h3>
        <span
          className={`subhealth-badge ${subscribed ? 'active' : 'inactive'}`}
          data-testid="subhealth-status-badge"
        >
          {subscribed ? 'Subscribed' : (status?.status || 'Free')}
        </span>
      </div>

      <div className="subhealth-grid">
        <div className="subhealth-cell">
          <span className="subhealth-label">Plan</span>
          <span className="subhealth-value" data-testid="subhealth-plan">
            {planLabel || '—'}
          </span>
        </div>
        <div className="subhealth-cell">
          <span className="subhealth-label">Next renewal</span>
          <span className="subhealth-value" data-testid="subhealth-renewal">
            {nextRenewal || '—'}
            {cancelAtEnd && <small className="subhealth-cancel"> (ending)</small>}
          </span>
        </div>
        <div className="subhealth-cell">
          <span className="subhealth-label">Owned skins</span>
          <span className="subhealth-value" data-testid="subhealth-skin-count">
            {health?.entitlements?.owned_skins_count ?? '—'}
          </span>
        </div>
        <div className="subhealth-cell">
          <span className="subhealth-label">Ranked pools</span>
          <span className="subhealth-value" data-testid="subhealth-pools">
            {health?.ranked_pools_present
              ? Object.entries(health.ranked_pools_present)
                  .filter(([, present]) => present)
                  .map(([p]) => p)
                  .join(', ') || 'None'
              : '—'}
          </span>
        </div>
      </div>

      {/* Warnings (self-heal surfaces data-integrity flags here) */}
      {health?.warnings?.length > 0 && (
        <ul className="subhealth-warnings" data-testid="subhealth-warnings">
          {health.warnings.map(w => (
            <li key={w} className="subhealth-warning">
              <span aria-hidden="true">⚠️</span>{' '}
              {WARNING_COPY[w] || `Data-integrity note: ${w}`}
            </li>
          ))}
        </ul>
      )}

      {/* Success / reconciled banner */}
      {health && health.warnings?.length === 0 && !error && (
        <p className="subhealth-success" data-testid="subhealth-all-good">
          ✅ All good — your account is fully in sync.
        </p>
      )}

      {error && (
        <p className="subhealth-error" data-testid="subhealth-error">{error}</p>
      )}

      <div className="subhealth-actions">
        <button
          type="button"
          className="subhealth-btn"
          onClick={runHealthCheck}
          disabled={loading}
          data-testid="subhealth-run-check"
        >
          {loading ? 'Checking…' : 'Run health check'}
        </button>
        <small className="subhealth-hint">
          Reconciles with Stripe and reports any entitlement issues.
        </small>
        <LastUpdatedIndicator />
      </div>
    </div>
  );
};

export default SubscriptionHealthWidget;
